import React, { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Dark gold premium dropdown — menu renders in a portal so it is not clipped by parent overflow.
 */
export function PremiumSelect({
  value,
  onValueChange,
  options = [],
  placeholder = 'Select…',
  className,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [menuRect, setMenuRect] = useState(null);
  const rootRef = useRef(null);
  const listId = useId();

  const updateMenuPosition = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const gap = 6;
    const maxHeight = 240;
    const spaceBelow = window.innerHeight - rect.bottom - gap;
    const spaceAbove = rect.top - gap;
    const openUp = spaceBelow < 160 && spaceAbove > spaceBelow;
    const height = Math.min(maxHeight, openUp ? spaceAbove : spaceBelow);

    setMenuRect({
      top: openUp ? rect.top - gap - height : rect.bottom + gap,
      left: rect.left,
      width: rect.width,
      maxHeight: Math.max(120, height),
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setMenuRect(null);
      return undefined;
    }
    updateMenuPosition();
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [open, options.length, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) {
        const menu = document.getElementById(listId);
        if (menu && menu.contains(e.target)) return;
        setOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('touchstart', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('touchstart', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, listId]);

  const display = value || placeholder;

  const menu = open && options.length > 0 && menuRect
    ? createPortal(
        <ul
          id={listId}
          role="listbox"
          style={{
            position: 'fixed',
            top: menuRect.top,
            left: menuRect.left,
            width: menuRect.width,
            maxHeight: menuRect.maxHeight,
            zIndex: 10000,
          }}
          className={cn(
            'overflow-y-auto overscroll-contain rounded-xl border border-[#C9A84C]/30',
            'bg-[#0a1628] py-1 shadow-[0_20px_48px_rgba(0,0,0,0.75)]',
          )}
        >
          {options.map((opt) => {
            const selected = opt === value;
            return (
              <li key={opt} role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    onValueChange(opt);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left text-sm transition-colors',
                    selected
                      ? 'bg-[#C9A84C]/18 text-[#f5e6b8]'
                      : 'text-white/80 hover:bg-white/[0.08] hover:text-[#f5e6b8]',
                  )}
                >
                  <span className="leading-snug">{opt}</span>
                  {selected && <Check className="h-4 w-4 shrink-0 text-[#C9A84C]" strokeWidth={2.5} />}
                </button>
              </li>
            );
          })}
        </ul>,
        document.body,
      )
    : null;

  return (
    <>
      <div ref={rootRef} className={cn('relative', className)}>
        <button
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          onClick={() => !disabled && setOpen((o) => !o)}
          className={cn(
            'flex h-11 w-full items-center justify-between rounded-xl border border-[#C9A84C]/35',
            'bg-[rgba(9,17,30,0.92)] px-4 text-left text-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]',
            'transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A84C]/45',
            'disabled:cursor-not-allowed disabled:opacity-50',
            open && 'border-[#C9A84C]/55 ring-2 ring-[#C9A84C]/20',
            value ? 'text-[#f8e6b6]' : 'text-white/45',
          )}
        >
          <span className="truncate pr-2">{display}</span>
          <ChevronDown
            className={cn(
              'h-4 w-4 shrink-0 text-[#C9A84C]/80 transition-transform duration-200',
              open && 'rotate-180',
            )}
            aria-hidden
          />
        </button>
      </div>
      {menu}
    </>
  );
}
